import {
  betslipToggle,
} from '@/betslip/betslip-store';
import {
  betslipToggleOptionsFromAccount,
  buildBetslipDraft,
} from '@/betslip/build-draft';
import { isSelectionInTicket, type BetslipTicket } from '@/domain/betslip';
import { getUserSettings, type Account } from '@/api/auth';
import type { EventOutcome } from '@/ui/sports/EventRow';

/** Mark outcomes selected when they exist on the current betslip. */
export function withBetslipSelectedState(
  ticket: BetslipTicket,
  eventId: string | number,
  outcomes: EventOutcome[],
): EventOutcome[] {
  return outcomes.map((outcome) => {
    if (outcome.state === 'locked' || outcome.state === 'suspended') return outcome;
    const marketId = outcome.marketId ?? outcome.id.split(':')[0];
    const outcomeCode = outcome.outcomeCode ?? outcome.outcome;
    const active = isSelectionInTicket(ticket, {
      eventId,
      marketId,
      specifier: outcome.specifier,
      outcome: outcomeCode,
    });
    return active ? { ...outcome, state: 'selected' as const } : outcome;
  });
}

export type BetslipEventSource = {
  EventId: number;
  Home?: string | null;
  Away?: string | null;
  HomeScore?: number | null;
  AwayScore?: number | null;
  Date?: string | null;
  Live?: boolean;
  Category?: string | null;
  Tournament?: string | null;
  TournamentId?: number | null;
  Sport?: string | null;
  SportId?: number;
};

/** Toggle a selection for an event/outcome pair (safe to call from map callbacks). */
export function selectOutcomeForEvent(
  event: BetslipEventSource,
  outcome: EventOutcome,
  account?: Account | null,
): void {
  const draft = buildBetslipDraft(event, outcome, {
    feedType: typeof account?.FeedType === 'number' ? account.FeedType : undefined,
  });
  if (!draft) return;
  betslipToggle(draft, betslipToggleOptionsFromAccount(account, getUserSettings()));
}

/** Season/outright row used as the slip event (EventId stays the tournament id). */
export type OutrightSeasonSource = {
  Id: number;
  Name: string;
  TournamentId: number;
  Tournament: string;
  Category?: string | null;
  Sport?: string | null;
  SportId?: number;
  EndDate?: string | null;
};

/** Toggle a season outright. Mixing with match selections is rejected by the slip. */
export function selectOutrightOutcome(
  season: OutrightSeasonSource,
  outcome: EventOutcome,
  account?: Account | null,
): void {
  const draft = buildBetslipDraft(
    {
      EventId: season.TournamentId,
      Home: season.Tournament,
      Away: season.Name,
      Date: season.EndDate ?? undefined,
      Live: false,
      Category: season.Category,
      Tournament: season.Tournament,
      TournamentId: season.TournamentId,
      Sport: season.Sport,
      SportId: season.SportId,
    },
    outcome,
    {
      outright: true,
      seasonId: season.Id,
      seasonName: season.Name,
      feedType: typeof account?.FeedType === 'number' ? account.FeedType : undefined,
    },
  );
  if (!draft) return;
  betslipToggle(draft, betslipToggleOptionsFromAccount(account, getUserSettings()));
}
